import React, { useEffect, useCallback } from 'react';
import { WizardProgressIndicator } from './WizardProgressIndicator';
import { WizardNavigation } from './WizardNavigation';
import { useWizardState } from '../../hooks/useWizardState';
import { useWizardValidation } from '../../hooks/useWizardValidation';
import { useWizardPersistence } from '../../hooks/useWizardPersistence';
import { useResponsiveLayout } from '../../hooks/useResponsiveLayout';
import type {
  FormWizardProps,
  WizardStepComponentProps
} from '../../types/WizardTypes';
import '../../../../styles/css/sidebar-layout.css';

/**
 * FormWizard - Multi-step form component with validation and responsive design
 * 
 * Features:
 * - Step navigation with validation
 * - Progress indication
 * - Data persistence
 * - Responsive layout (tabs on desktop, accordion on mobile)
 * - Custom validation rules
 * - Skip functionality
 */
export const FormWizard: React.FC<FormWizardProps> = ({
  steps,
  navigation = {},
  callbacks = {},
  initialStep = 0,
  initialData = {},
  loading = false,
  className = '',
  variant = 'auto',
  persistData = false,
  persistKey = 'formWizard',
  headless = false,
  sidebar,
  showSidebar = false
}) => {
  // Determine layout based on variant and screen size
  const layout = useResponsiveLayout(variant);
  
  // Initialize wizard state
  const {
    state,
    setState,
    setCurrentStep,
    setStepData,
    setStepCompleted,
    setStepErrors,
    setGlobalErrors
  } = useWizardState({
    steps,
    initialStep,
    initialData
  });

  // Validation hook
  const {
    validateStep,
    validateAllSteps,
    clearStepErrors
  } = useWizardValidation(steps);

  // Persistence hook
  const {
    saveData,
    loadData,
    clearData
  } = useWizardPersistence(persistKey, persistData);

  // Load persisted data on mount
  useEffect(() => {
    if (persistData) {
      const savedData = loadData();
      if (savedData) {
        setState(prevState => ({
          ...prevState,
          stepData: { ...prevState.stepData, ...savedData.stepData },
          stepCompleted: { ...prevState.stepCompleted, ...savedData.stepCompleted },
          currentStep: savedData.currentStep || initialStep
        }));
      }
    }
  }, [persistData, loadData, setState, initialStep]);

  // Save data when state changes
  useEffect(() => {
    if (persistData && state.stepData) {
      saveData({
        stepData: state.stepData,
        stepCompleted: state.stepCompleted,
        currentStep: state.currentStep
      });
    }
  }, [state.stepData, state.stepCompleted, state.currentStep, persistData, saveData]);

  // Get current step
  const currentStepIndex = state.currentStep;
  const currentStep = steps[currentStepIndex];
  const currentStepData = state.stepData[currentStep?.id] || {};

  // Navigation handlers
  const goToStep = useCallback(async (stepIndex: number) => {
    if (stepIndex < 0 || stepIndex >= steps.length) {
      return false;
    }

    // Validate current step before moving forward
    if (stepIndex > currentStepIndex) {
      const currentStepId = steps[currentStepIndex].id;
      const validationResult = await validateStep(currentStepId, currentStepData);
      
      if (validationResult !== true) {
        setStepErrors(currentStepId, Array.isArray(validationResult) ? validationResult : [validationResult]);
        callbacks.onValidationError?.(currentStepId, Array.isArray(validationResult) ? validationResult : [validationResult]);
        return false;
      }
      
      // Mark current step as completed
      setStepCompleted(currentStepId, true);
      clearStepErrors(currentStepId);
    }

    setCurrentStep(stepIndex);
    callbacks.onStepChange?.(stepIndex, steps[stepIndex].id);
    return true;
  }, [currentStepIndex, steps, validateStep, currentStepData, setStepErrors, callbacks, setStepCompleted, clearStepErrors, setCurrentStep]);

  const goToNext = useCallback(() => {
    return goToStep(currentStepIndex + 1);
  }, [currentStepIndex, goToStep]);

  const goToPrevious = useCallback(() => {
    return goToStep(currentStepIndex - 1);
  }, [currentStepIndex, goToStep]);

  // Data handling
  const handleDataChange = useCallback((data: any) => {
    const stepId = currentStep.id;
    setStepData(stepId, data);
    // Clear errors when data changes (user is fixing issues)
    clearStepErrors(stepId);
    callbacks.onDataChange?.(stepId, data, { ...state.stepData, [stepId]: data });
  }, [currentStep, setStepData, clearStepErrors, callbacks, state.stepData]);

  // Submit handler
  const handleSubmit = useCallback(async () => {
    setState(prev => ({ ...prev, isLoading: true }));
    
    try {
      // Validate all steps
      const validationResults = await validateAllSteps(state.stepData);
      const hasErrors = Object.values(validationResults).some(result => result !== true);
      
      if (hasErrors) {
        // Set errors for each step
        Object.entries(validationResults).forEach(([stepId, result]) => {
          if (result !== true) {
            setStepErrors(stepId, Array.isArray(result) ? result : [result]);
          }
        });
        setState(prev => ({ ...prev, isLoading: false }));
        return;
      }

      // All validation passed, submit the wizard
      await callbacks.onSubmit?.(state.stepData);
      
      // Clear persisted data after successful submit
      if (persistData) {
        clearData();
      }
    } catch (error) {
      console.error('Wizard submission error:', error);
      setGlobalErrors([error instanceof Error ? error.message : 'An error occurred']);
    } finally {
      setState(prev => ({ ...prev, isLoading: false }));
    }
  }, [state.stepData, validateAllSteps, callbacks, setStepErrors, setState, persistData, clearData, setGlobalErrors]);

  // Step component props
  const stepComponentProps: WizardStepComponentProps = {
    data: currentStepData,
    onDataChange: handleDataChange,
    allData: state.stepData,
    onNext: goToNext,
    onPrevious: goToPrevious,
    onSubmit: handleSubmit,
    isFirst: currentStepIndex === 0,
    isLast: currentStepIndex === steps.length - 1,
    stepIndex: currentStepIndex,
    totalSteps: steps.length,
    errors: state.stepErrors[currentStep?.id] || []
  };

  if (!currentStep) {
    return null;
  }

  // Headless mode: render progress indicator, step content, and navigation with minimal container
  if (headless) {
    return (
      <div className="tw-w-full">
        {/* Progress Indicator */}
        <WizardProgressIndicator
          steps={steps}
          currentStep={currentStepIndex}
          stepCompleted={state.stepCompleted}
          stepErrors={state.stepErrors}
          onStepClick={(stepIndex) => goToStep(stepIndex)}
          layout={layout}
        />

        {/* Main Content */}
        <main className="tw-flex-1 tw-overflow-y-auto tw-bg-[#f6f7f8] dark:tw-bg-[#101922]">
          <div className="tw-p-4 md:tw-p-6">
            <div className={sidebar && showSidebar ? "wizard-with-sidebar" : "tw-max-w-4xl tw-mx-auto"}>
            {/* Main Wizard Content */}
            <div className={sidebar && showSidebar ? "wizard-main-content" : "tw-w-full"}>
              <div className="tw-space-y-6">
                {/* Global Errors */}
                {state.globalErrors.length > 0 && (
                  <div className="tw-rounded-lg tw-bg-red-50 dark:tw-bg-red-900/20 tw-border tw-border-red-200 dark:tw-border-red-700 tw-p-4">
                    <div className="tw-flex">
                      <span className="material-symbols-outlined tw-text-red-400 tw-mr-3 tw-mt-0.5">
                        error
                      </span>
                      <div className="tw-text-sm">
                        {state.globalErrors.map((error, index) => (
                          <div key={index} className="tw-text-red-600 dark:tw-text-red-400">
                            {error}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Step Component */}
                <currentStep.component {...stepComponentProps} />
              </div>
            </div>

            {/* Optional Sidebar */}
            {sidebar && showSidebar && (
              <div className="analytics-sidebar">
                {sidebar}
              </div>
            )}
          </div>
          </div>

          {/* Navigation Footer */}
          <div className="tw-mt-8 tw-border-t tw-border-gray-200 dark:tw-border-[#233648] tw-bg-white dark:tw-bg-[#1C252E] tw-px-4 md:tw-px-6 tw-py-4">
            <div className={sidebar && showSidebar ? "wizard-with-sidebar" : "tw-max-w-4xl tw-mx-auto"}>
              <div className={sidebar && showSidebar ? "wizard-main-content" : "tw-w-full"}>
              <div className="tw-w-full tw-flex tw-items-center tw-justify-between">
                <div className="tw-flex tw-items-center tw-gap-3">
                  <button
                    type="button"
                    onClick={callbacks.onCancel}
                    className="tw-px-4 md:tw-px-6 tw-py-2.5 tw-rounded-lg tw-border tw-border-gray-300 dark:tw-border-[#364152] tw-text-sm tw-font-semibold tw-text-gray-700 dark:tw-text-gray-300 hover:tw-bg-gray-100 dark:hover:tw-bg-[#233648] tw-transition-colors"
                  >
                    {navigation.cancelButtonText || 'Cancel'}
                  </button>
                  <button
                    type="button"
                    onClick={goToPrevious}
                    disabled={currentStepIndex === 0}
                    className={`tw-px-4 md:tw-px-6 tw-py-2.5 tw-rounded-lg tw-border tw-border-gray-300 dark:tw-border-[#364152] tw-text-sm tw-font-semibold tw-transition-colors ${
                      currentStepIndex === 0
                        ? "tw-text-gray-400 dark:tw-text-gray-600 tw-cursor-not-allowed tw-bg-gray-50 dark:tw-bg-gray-800"
                        : "tw-text-gray-700 dark:tw-text-gray-300 hover:tw-bg-gray-100 dark:hover:tw-bg-[#233648]"
                    }`}
                  >
                    {navigation.previousButtonText || 'Previous Step'}
                  </button>
                </div>
                <button
                  type="button"
                  onClick={
                    currentStepIndex === steps.length - 1
                      ? handleSubmit
                      : goToNext
                  }
                  disabled={state.isLoading || loading}
                  className={`tw-px-4 md:tw-px-6 tw-py-2.5 tw-rounded-lg tw-bg-primary tw-text-sm tw-font-semibold tw-text-white tw-shadow-lg tw-shadow-primary/30 hover:tw-bg-primary/90 tw-transition-colors ${
                    state.isLoading || loading
                      ? "tw-opacity-50 tw-cursor-not-allowed"
                      : ""
                  }`}
                >
                  {state.isLoading || loading
                    ? "Processing..."
                    : currentStepIndex === steps.length - 1
                    ? navigation.submitButtonText || 'Submit'
                    : navigation.nextButtonText || 'Next Step'}
                </button>
              </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  const containerClasses = [
    'tw-w-full tw-max-w-4xl tw-mx-auto tw-bg-white dark:tw-bg-surface-dark tw-rounded-xl tw-shadow-sm tw-border tw-border-gray-200 dark:tw-border-gray-700 tw-overflow-hidden',
    `formwizard--${layout}`,
    className
  ].filter(Boolean).join(' ');

  return (
    <div className={containerClasses}>
      {/* Progress Indicator */}
      <WizardProgressIndicator
        steps={steps}
        currentStep={currentStepIndex}
        stepCompleted={state.stepCompleted}
        stepErrors={state.stepErrors}
        onStepClick={(stepIndex) => goToStep(stepIndex)}
        layout={layout}
      />

      {/* Step Content */}
      <div className="tw-p-6 md:tw-p-8">
        <div className="tw-space-y-6">
          {/* Step Header */}
          <div className="tw-text-center md:tw-text-left">
            <h2 className="tw-text-2xl tw-font-bold tw-text-gray-900 dark:tw-text-white">
              {currentStep.title}
            </h2>
            {currentStep.subtitle && (
              <p className="tw-mt-2 tw-text-sm tw-text-gray-500 dark:tw-text-gray-400">
                {currentStep.subtitle}
              </p>
            )}
          </div>

          {/* Global Errors */}
          {state.globalErrors.length > 0 && (
            <div className="tw-rounded-lg tw-bg-red-50 dark:tw-bg-red-900/20 tw-border tw-border-red-200 dark:tw-border-red-700 tw-p-4">
              <div className="tw-flex">
                <span className="material-symbols-outlined tw-text-red-400 tw-mr-3 tw-mt-0.5">
                  error
                </span>
                <div className="tw-text-sm">
                  {state.globalErrors.map((error, index) => (
                    <div key={index} className="tw-text-red-600 dark:tw-text-red-400">
                      {error}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step Component */}
          <div className="tw-min-h-[300px]">
            <currentStep.component {...stepComponentProps} />
          </div>
        </div>
      </div>

      {/* Navigation */}
      <WizardNavigation
        currentStep={currentStepIndex}
        totalSteps={steps.length}
        isLoading={state.isLoading || loading}
        onNext={goToNext}
        onPrevious={goToPrevious}
        onSubmit={handleSubmit}
        onCancel={callbacks.onCancel}
        config={navigation}
        hasErrors={state.stepErrors[currentStep.id]?.length > 0}
      />
    </div>
  );
};